import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QuoteModal from './QuoteModal';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const trackEvent = (eventName: string, params: Record<string, unknown>) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

interface HeaderProps {
  lang?: 'en' | 'tr';
}

const NAV_LABELS: Record<'en' | 'tr', Record<string, string>> = {
  en: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    portfolio: 'Portfolio',
    contact: 'Contact',
    requestQuote: 'Request Quote',
  },
  tr: {
    home: 'Ana Sayfa',
    about: 'Hakkımızda',
    services: 'Hizmetler',
    portfolio: 'Portföy',
    contact: 'İletişim',
    requestQuote: 'Teklif Al',
  },
};

export default function Header({ lang = 'en' }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const navigate = useNavigate();
  const labels = NAV_LABELS[lang];

  const scrollToSection = (sectionId: string) => {
    trackEvent('navigation_click', {
      event_category: 'navigation',
      event_label: sectionId,
      value: 1
    });
    setIsMenuOpen(false);
    const homePath = lang === 'tr' ? '/tr' : '/';
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`${homePath}#${sectionId}`);
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const switchLang = (target: 'en' | 'tr') => {
    if (target === lang) return;
    // Kullanıcı manuel dil seçti — geo-redirect bir daha çalışmasın
    sessionStorage.setItem('geo_redirect_done', 'done');
    navigate(target === 'tr' ? '/tr' : '/');
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-md border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/83cfc8c5c5f2a996af905797f39a3554.png"
                alt="IEM101 Logo"
                className="h-8 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {['home', 'about', 'services', 'portfolio', 'contact'].map((section) => {
                if (section === 'services') {
                  const servicesPath = lang === 'tr' ? '/tr/hizmetler' : '/services';
                  return (
                    <button
                      key={section}
                      onClick={() => { trackEvent('navigation_click', { event_category: 'navigation', event_label: section, value: 1 }); navigate(servicesPath); }}
                      className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer capitalize"
                    >
                      {labels[section]}
                    </button>
                  );
                }
                return (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer capitalize"
                  >
                    {labels[section]}
                  </button>
                );
              })}
            </nav>

            {/* CTA + Language + Mobile Menu */}
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <div className="hidden md:flex items-center bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => switchLang('en')}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    lang === 'en'
                      ? 'bg-yellow-400 text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLang('tr')}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    lang === 'tr'
                      ? 'bg-yellow-400 text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  TR
                </button>
              </div>

              <button
                onClick={() => setIsQuoteOpen(true)}
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black text-sm font-semibold rounded-lg hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-text-line"></i>
                {labels.requestQuote}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-white hover:text-yellow-400 transition-colors"
              >
                <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-800">
              <nav className="flex flex-col space-y-4">
                {['home', 'about', 'services', 'portfolio', 'contact'].map((section) => {
                  if (section === 'services') {
                    const servicesPath = lang === 'tr' ? '/tr/hizmetler' : '/services';
                    return (
                      <button
                        key={section}
                        onClick={() => { navigate(servicesPath); setIsMenuOpen(false); }}
                        className="text-gray-300 hover:text-yellow-400 transition-colors capitalize text-left cursor-pointer"
                      >
                        {labels[section]}
                      </button>
                    );
                  }
                  return (
                    <a
                      key={section}
                      href={`#${section}`}
                      className="text-gray-300 hover:text-yellow-400 transition-colors capitalize"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {labels[section]}
                    </a>
                  );
                })}
                {/* Mobile Language Switcher */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => switchLang('en')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      lang === 'en' ? 'bg-yellow-400 text-black' : 'border border-gray-700 text-gray-400'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => switchLang('tr')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      lang === 'tr' ? 'bg-yellow-400 text-black' : 'border border-gray-700 text-gray-400'
                    }`}
                  >
                    Türkçe
                  </button>
                </div>
                <button
                  onClick={() => { setIsQuoteOpen(true); setIsMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black text-sm font-semibold rounded-lg w-full justify-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-text-line"></i>
                  {labels.requestQuote}
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <QuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} lang={lang} />
    </>
  );
}
